import { useState, useEffect, useCallback } from 'react';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { getApiKeys, createApiKey, revokeApiKey } from '../services/apiService';
import styles from './DeveloperPage.module.css';

const NewApiKeyModal = ({ onClose, onKeyCreated }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [keyName, setKeyName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!keyName.trim()) {
      setError('Key name is required.');
      return;
    }
    setIsCreating(true);
    setError('');
    try {
      const accessToken = await getAccessTokenSilently();
      const newKeyData = await createApiKey({ keyName }, accessToken);
      onKeyCreated(newKeyData);
    } catch (err) {
      setError('Failed to create key.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className={styles.modalContent}>
      <h3>Generate New API Key</h3>
      <p>Give your key a descriptive name to help you identify it later.</p>
      <input
        type="text"
        value={keyName}
        onChange={(e) => setKeyName(e.target.value)}
        placeholder="e.g., My n8n Connector"
      />
      {error && <p className="errorText">{error}</p>}
      <div className={styles.modalActions}>
        <button onClick={onClose} className="action-button secondary">
          Cancel
        </button>
        <button
          onClick={handleCreate}
          className="action-button primary"
          disabled={isCreating}
        >
          {isCreating ? 'Generating...' : 'Generate Key'}
        </button>
      </div>
    </div>
  );
};

const ShowApiKeyModal = ({ apiKeyData, onClose }) => {
  const [copyText, setCopyText] = useState('Copy');
  const handleCopy = () => {
    navigator.clipboard.writeText(apiKeyData.apiKey);
    setCopyText('Copied!');
    setTimeout(() => setCopyText('Copy'), 2000);
  };

  return (
    <div className={styles.modalContent}>
      <h3>API Key Generated</h3>
      <p className={styles.warningText}>
        Please save this secret key somewhere safe. You will not be able to see
        it again.
      </p>
      <div className={styles.keyDisplay}>
        <input type="text" readOnly value={apiKeyData.apiKey} />
        <button onClick={handleCopy} className="action-button secondary">
          {copyText}
        </button>
      </div>
      <div className={styles.modalActions}>
        <button onClick={onClose} className="action-button primary">
          I have saved my key
        </button>
      </div>
    </div>
  );
};

function DeveloperPage() {
  const { getAccessTokenSilently } = useAuth0();
  const [keys, setKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const fetchKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const accessToken = await getAccessTokenSilently();
      const apiKeys = await getApiKeys(accessToken);
      setKeys(apiKeys);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleKeyCreated = (newKeyData) => {
    setNewKey(newKeyData);
    setShowCreateModal(false);
    fetchKeys();
  };

  const handleRevokeKey = async (keyId) => {
    if (
      window.confirm(
        'Are you sure you want to revoke this key? This action cannot be undone.'
      )
    ) {
      try {
        const accessToken = await getAccessTokenSilently();
        await revokeApiKey(keyId, accessToken);
        fetchKeys();
      } catch (error) {
        console.error('Failed to revoke key:', error);
      }
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>Developer Settings</h2>
        <a
          href={`${process.env.REACT_APP_API_BASE_URL}/docs`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <button className="action-button secondary">
            <i className="fas fa-book-open" style={{ marginRight: '8px' }}></i>
            View API Reference
          </button>
        </a>
      </div>
      <div className={styles.developerContainer}>
        <div className={styles.apiKeysSection}>
          <h3>API Keys</h3>
          <p className={styles.description}>
            Manage your API keys to integrate the Ego Protocol with your
            applications.
          </p>

          {isLoading ? (
            <p>Loading API keys...</p>
          ) : (
            <div className={styles.keysTable}>
              {keys.length > 0 ? (
                keys.map((key) => (
                  <div key={key.keyId} className={styles.keyRow}>
                    <span className={styles.keyName}>{key.keyName}</span>
                    <span className={styles.keyPrefix}>{key.keyPrefix}</span>
                    <span className={styles.keyDate}>
                      Created on{' '}
                      {new Date(key.createdAt * 1000).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() => handleRevokeKey(key.keyId)}
                      className={styles.revokeButton}
                    >
                      Revoke
                    </button>
                  </div>
                ))
              ) : (
                <p className={styles.noKeysMessage}>
                  You haven't generated any API keys yet.
                </p>
              )}
            </div>
          )}

          <button
            onClick={() => setShowCreateModal(true)}
            className="action-button primary"
            style={{ marginTop: '1.5rem' }}
          >
            Generate New Key
          </button>
        </div>

        <div className={styles.apiUsageSection}>
          <h3>API Usage</h3>
          <p className={styles.description}>
            Your API usage for the current billing cycle.
          </p>
          <div className={styles.chartPlaceholder}>
            <p>Usage chart coming soon</p>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className={styles.modalOverlay}>
          <NewApiKeyModal
            onClose={() => setShowCreateModal(false)}
            onKeyCreated={handleKeyCreated}
          />
        </div>
      )}

      {newKey && (
        <div className={styles.modalOverlay}>
          <ShowApiKeyModal
            apiKeyData={newKey}
            onClose={() => setNewKey(null)}
          />
        </div>
      )}
    </div>
  );
}

export default withAuthenticationRequired(DeveloperPage);
